import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Space, Spin, Tooltip, Typography, message } from 'antd';
import { ScissorOutlined, AppstoreOutlined, PictureOutlined, ExpandOutlined, UploadOutlined } from '@ant-design/icons';
import { ingestionApi } from '../ingestionApi';
import { catalogImageUrl } from '../../images';
import { ImageCropModal } from './ImageCropModal';

const { Text } = Typography;

// Types whose thumbnail is the texture itself (tileable / mural actions). A
// pillow's thumbnail is a cutout of the pillow; its flattened cover is made in
// step 4, so it gets the plain product-image actions.
const TEXTURE_TYPES = ['Texture', 'Curtain'];

/**
 * Main thumbnail + scraped-image gallery for a (primary) variation — the catalog
 * displays the variation image, so its thumbnail and image actions live here, not
 * on individual variants. The gallery pools the candidate images of every variant
 * in the variation; the chosen image is applied to all of them. The admin can pick
 * any scraped image as the main thumbnail, remove its background, or (wall prints)
 * extract the artwork from its in-room photo.
 */
export const VariationImagePanel = ({ item, variationId, onChanged }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [cropOpen, setCropOpen] = useState(false);
    const uploadRef = useRef(null);

    const isWallPrint = item.itemType === 'WallPrint';
    const isTexture = TEXTURE_TYPES.includes(item.itemType);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await ingestionApi.getVariationImages(item.id, variationId));
        } catch (e) {
            message.error(e.message || 'Failed to load images');
        } finally {
            setLoading(false);
        }
    }, [item.id, variationId]);

    useEffect(() => {
        load();
    }, [load]);

    const imageId = data?.imageId ?? null;
    const imageLabel = data?.imageLabel ?? null;
    const candidates = data?.candidates || [];
    const mainSourceUrl = data?.mainSourceUrl || null;
    // The LLM's tileable-vs-mural call for textures: null until extraction runs.
    const repeatable = data?.repeat !== false;

    // When the active image is a generated result (make-tileable / mural / cutout),
    // show it as its own gallery thumbnail (added, not just swapped in as the main).
    const GENERATED_LABELS = ['texture-gen', 'wallprint', 'cutout'];
    const isGeneratedMain = !!imageId && GENERATED_LABELS.includes(imageLabel);
    const galleryItems = [
        ...(isGeneratedMain ? [{ key: '__generated__', url: catalogImageUrl(imageId, 112), current: true, generated: true }] : []),
        ...candidates.map((url) => ({ key: url, url, current: !isGeneratedMain && url === mainSourceUrl, generated: false })),
    ];

    // After an image action, reload so the gallery reflects the new active image
    // (and shows a generated result as its own thumbnail) rather than only swapping
    // the main thumbnail.
    const setMain = async (sourceUrl) => {
        setBusy(sourceUrl);
        try {
            await ingestionApi.setVariationThumbnail(item.id, variationId, sourceUrl);
            await load();
            onChanged?.();
            message.success('Main thumbnail updated');
        } catch (e) {
            message.error(e.message || 'Failed to set thumbnail');
        } finally {
            setBusy(null);
        }
    };

    const removeBackground = async () => {
        setBusy('__bg__');
        try {
            await ingestionApi.removeVariationBackground(item.id, variationId);
            await load();
            onChanged?.();
            message.success('Background removed');
        } catch (e) {
            message.error(e.message || 'Failed to remove background');
        } finally {
            setBusy(null);
        }
    };

    const extractWallPrint = async () => {
        setBusy('__wp__');
        try {
            await ingestionApi.extractVariationWallPrint(item.id, variationId);
            await load();
            onChanged?.();
            message.success(isWallPrint ? 'Wall print extracted' : 'Mural extracted');
        } catch (e) {
            message.error(e.message || 'Failed to extract');
        } finally {
            setBusy(null);
        }
    };

    const generateTexture = async () => {
        setBusy('__tile__');
        try {
            await ingestionApi.generateVariationTexture(item.id, variationId);
            await load();
            onChanged?.();
            message.success('Tileable texture generated');
        } catch (e) {
            message.error(e.message || 'Failed to generate texture');
        } finally {
            setBusy(null);
        }
    };

    const uploadImage = async (file) => {
        setBusy('__upload__');
        try {
            await ingestionApi.uploadVariationImage(item.id, variationId, file);
            await load();
            onChanged?.();
            message.success('Image uploaded');
        } catch (e) {
            message.error(e.message || 'Failed to upload image');
        } finally {
            setBusy(null);
        }
    };

    const cropImage = async (rect) => {
        setBusy('__crop__');
        try {
            await ingestionApi.cropVariationImage(item.id, variationId, rect);
            await load();
            onChanged?.();
            setCropOpen(false);
            message.success('Image cropped');
        } catch (e) {
            message.error(e.message || 'Failed to crop');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div style={{ width: 200, flexShrink: 0 }}>
            <div
                style={{
                    width: 200,
                    height: 200,
                    borderRadius: 6,
                    background: '#fafafa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {imageId ? (
                    <img
                        src={catalogImageUrl(imageId, 200)}
                        alt="variation thumbnail"
                        style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                ) : (
                    <Text type="secondary">no image</Text>
                )}
            </div>

            {isWallPrint ? (
                <Space direction="vertical" size={6} style={{ marginTop: 8, width: '100%' }}>
                    <Button
                        icon={<ScissorOutlined />}
                        size="small"
                        block
                        disabled={!imageId}
                        loading={busy === '__wp__'}
                        onClick={extractWallPrint}
                    >
                        Extract wall print
                    </Button>
                    <Button
                        icon={<ExpandOutlined />}
                        size="small"
                        block
                        disabled={!imageId}
                        loading={busy === '__crop__'}
                        onClick={() => setCropOpen(true)}
                    >
                        Crop
                    </Button>
                </Space>
            ) : isTexture ? (
                // The LLM picks tileable-vs-mural automatically; the admin can override
                // here. The recommended action (per `repeat`) is the primary button.
                <Space direction="vertical" size={6} style={{ marginTop: 8, width: '100%' }}>
                    <Button
                        icon={<AppstoreOutlined />}
                        type={repeatable ? 'primary' : 'default'}
                        size="small"
                        block
                        disabled={!imageId}
                        loading={busy === '__tile__'}
                        onClick={generateTexture}
                    >
                        Make tileable
                    </Button>
                    <Button
                        icon={<PictureOutlined />}
                        type={!repeatable ? 'primary' : 'default'}
                        size="small"
                        block
                        disabled={!imageId}
                        loading={busy === '__wp__'}
                        onClick={extractWallPrint}
                    >
                        Extract mural
                    </Button>
                    <Button
                        icon={<ExpandOutlined />}
                        size="small"
                        block
                        disabled={!imageId}
                        loading={busy === '__crop__'}
                        onClick={() => setCropOpen(true)}
                    >
                        Crop
                    </Button>
                </Space>
            ) : (
                <Space direction="vertical" size={6} style={{ marginTop: 8, width: '100%' }}>
                    <Button
                        icon={<ScissorOutlined />}
                        size="small"
                        block
                        disabled={!imageId}
                        loading={busy === '__bg__'}
                        onClick={removeBackground}
                    >
                        Remove background
                    </Button>
                    <Button
                        icon={<ExpandOutlined />}
                        size="small"
                        block
                        disabled={!imageId}
                        loading={busy === '__crop__'}
                        onClick={() => setCropOpen(true)}
                    >
                        Crop
                    </Button>
                </Space>
            )}

            <input
                ref={uploadRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file);
                    e.target.value = '';
                }}
            />
            <Button
                icon={<UploadOutlined />}
                size="small"
                block
                style={{ marginTop: 6 }}
                loading={busy === '__upload__'}
                onClick={() => uploadRef.current?.click()}
            >
                Upload image
            </Button>

            <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Images
                </Text>
                {loading ? (
                    <div style={{ padding: 12, textAlign: 'center' }}>
                        <Spin size="small" />
                    </div>
                ) : galleryItems.length === 0 ? (
                    <div style={{ fontSize: 12 }}>
                        <Text type="secondary">none found</Text>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                        {galleryItems.map((it) => {
                            const clickable = !it.generated && !it.current;
                            const title = it.generated
                                ? 'Generated result (current)'
                                : it.current
                                  ? 'Current main image'
                                  : 'Set as main thumbnail';
                            return (
                                <Tooltip key={it.key} title={title}>
                                    <div
                                        onClick={() => clickable && !busy && setMain(it.url)}
                                        style={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 4,
                                            overflow: 'hidden',
                                            cursor: clickable ? 'pointer' : 'default',
                                            border: it.current ? '2px solid #1677ff' : '1px solid #eee',
                                            opacity: busy && busy !== it.url ? 0.5 : 1,
                                            position: 'relative',
                                        }}
                                    >
                                        <img
                                            src={it.url}
                                            alt={it.generated ? 'generated' : 'scraped'}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        {busy === it.url && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: 'rgba(255,255,255,0.6)',
                                                }}
                                            >
                                                <Spin size="small" />
                                            </div>
                                        )}
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </div>
                )}
            </div>

            <ImageCropModal
                open={cropOpen}
                imageId={imageId}
                busy={busy === '__crop__'}
                onCancel={() => setCropOpen(false)}
                onConfirm={cropImage}
            />
        </div>
    );
};
